export type ReminderStrength = 'once' | 'standard' | 'pester';
export type ReminderCaptureKind = 'event' | 'quick_reminder';
export type ReminderEventType = 'appointment' | 'school' | 'bill' | 'birthday' | 'other';
export type ReminderPreset = 'later_today' | 'tomorrow_morning' | 'next_week' | 'next_month' | 'custom';
export type ReminderRecurrenceUnit = 'days' | 'weeks' | 'months' | 'years';

export type ReminderRecurrence = {
  recurrenceType: 'one-off' | 'custom';
  recurrenceInterval: number | null;
  recurrenceUnit: ReminderRecurrenceUnit | null;
  recurrenceDayOfWeek: number[] | null;
  recurrenceWeekOfMonth: string | null;
  recurrenceMonthDay: number | null;
  summary: string;
};

export type ParsedQuickReminder = {
  rawInput: string;
  title: string;
  date: string;
  time: string;
  type: ReminderEventType;
  captureKind: ReminderCaptureKind;
  reminderStrength: ReminderStrength;
  recurrence: ReminderRecurrence;
  preset: ReminderPreset | null;
  dateLabel: string;
  repeatLabel: string;
};

export type QuickReminderEventLike = {
  title: string;
  date: string;
  time?: string | null;
  captureKind?: string | null;
  capture_kind?: string | null;
  reminderStrength?: ReminderStrength | null;
  reminder_strength?: ReminderStrength | null;
};

export const REMINDER_STRENGTH_OPTIONS: Array<{
  value: ReminderStrength;
  label: string;
  description: string;
}> = [
  { value: 'once', label: 'Once', description: 'One calm reminder on the day.' },
  { value: 'standard', label: 'Standard', description: 'A reminder, then a follow-up nudge if it is still hanging around.' },
  { value: 'pester', label: 'Pester', description: 'A reminder, a follow-up, and one more same-day prompt.' },
];

export const REMINDER_PRESETS: Array<{
  value: ReminderPreset;
  label: string;
}> = [
  { value: 'later_today', label: 'Later today' },
  { value: 'tomorrow_morning', label: 'Tomorrow morning' },
  { value: 'next_week', label: 'Next week' },
  { value: 'next_month', label: 'Next month' },
  { value: 'custom', label: 'Custom' },
];

const WEEKDAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function pad(value: number) {
  return String(value).padStart(2, '0');
}

function cloneDate(value: Date) {
  return new Date(value.getTime());
}

function startOfLocalDay(value: Date) {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

function toIsoDate(value: Date) {
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

function toTimeValue(hours: number, minutes = 0) {
  return `${pad(hours)}:${pad(minutes)}`;
}

function setTime(value: Date, hours: number, minutes = 0) {
  const next = cloneDate(value);
  next.setHours(hours, minutes, 0, 0);
  return next;
}

function addDays(value: Date, amount: number) {
  const next = cloneDate(value);
  next.setDate(next.getDate() + amount);
  return next;
}

function addMonths(value: Date, amount: number) {
  const next = cloneDate(value);
  next.setMonth(next.getMonth() + amount);
  return next;
}

function weekdayIndexFromDate(value: Date) {
  return (value.getDay() + 6) % 7;
}

function differenceInDays(left: Date, right: Date) {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((startOfLocalDay(left).getTime() - startOfLocalDay(right).getTime()) / msPerDay);
}

function formatDisplayDate(date: string, time: string) {
  const value = new Date(`${date}T${time || '09:00'}:00`);
  const dateLabel = value.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  return time ? `${dateLabel} at ${time.slice(0, 5)}` : dateLabel;
}

function normaliseInput(value: string) {
  return value.replace(/\s+/g, ' ').trim();
}

function removePhrase(value: string, phrase: string) {
  if (!phrase) return value;
  return value.replace(phrase, ' ');
}

function detectReminderType(title: string): ReminderEventType {
  const value = title.toLowerCase();
  if (/\bbirthday\b/.test(value)) return 'birthday';
  if (/\b(gp|doctor|dentist|hospital|clinic|appointment|call the gp|call gp|optician|blood test|smear|medication)\b/.test(value)) {
    return 'appointment';
  }
  if (/\b(pay|bill|invoice|meter|rent|council tax|school trip|deposit|submit meter reading)\b/.test(value)) {
    return 'bill';
  }
  if (/\b(school|parents evening|trip|uniform|childcare|pickup|drop off|drop-off)\b/.test(value)) {
    return 'school';
  }
  return 'other';
}

function buildOneOffRecurrence(): ReminderRecurrence {
  return {
    recurrenceType: 'one-off',
    recurrenceInterval: null,
    recurrenceUnit: null,
    recurrenceDayOfWeek: null,
    recurrenceWeekOfMonth: null,
    recurrenceMonthDay: null,
    summary: 'Does not repeat',
  };
}

function buildMonthlyRecurrence(referenceDate: Date): ReminderRecurrence {
  return {
    recurrenceType: 'custom',
    recurrenceInterval: 1,
    recurrenceUnit: 'months',
    recurrenceDayOfWeek: null,
    recurrenceWeekOfMonth: null,
    recurrenceMonthDay: referenceDate.getDate(),
    summary: 'Repeats every month',
  };
}

function detectRecurrence(normalized: string, referenceDate: Date) {
  if (/\b(every day|daily|repeat daily|repeat every day)\b/i.test(normalized)) {
    return {
      match: normalized.match(/\b(every day|daily|repeat daily|repeat every day)\b/i)?.[0] || '',
      recurrence: {
        recurrenceType: 'custom' as const,
        recurrenceInterval: 1,
        recurrenceUnit: 'days' as const,
        recurrenceDayOfWeek: null,
        recurrenceWeekOfMonth: null,
        recurrenceMonthDay: null,
        summary: 'Repeats every day',
      },
    };
  }

  if (/\b(every week|weekly|repeat weekly|repeat every week)\b/i.test(normalized)) {
    return {
      match: normalized.match(/\b(every week|weekly|repeat weekly|repeat every week)\b/i)?.[0] || '',
      recurrence: {
        recurrenceType: 'custom' as const,
        recurrenceInterval: 1,
        recurrenceUnit: 'weeks' as const,
        recurrenceDayOfWeek: [weekdayIndexFromDate(referenceDate)],
        recurrenceWeekOfMonth: null,
        recurrenceMonthDay: null,
        summary: 'Repeats every week',
      },
    };
  }

  if (/\b(every month|monthly|repeat every month|repeat monthly)\b/i.test(normalized)) {
    return {
      match: normalized.match(/\b(every month|monthly|repeat every month|repeat monthly)\b/i)?.[0] || '',
      recurrence: buildMonthlyRecurrence(referenceDate),
    };
  }

  if (/\b(every year|yearly|annually|repeat every year)\b/i.test(normalized)) {
    return {
      match: normalized.match(/\b(every year|yearly|annually|repeat every year)\b/i)?.[0] || '',
      recurrence: {
        recurrenceType: 'custom' as const,
        recurrenceInterval: 1,
        recurrenceUnit: 'years' as const,
        recurrenceDayOfWeek: [referenceDate.getMonth()],
        recurrenceWeekOfMonth: null,
        recurrenceMonthDay: referenceDate.getDate(),
        summary: 'Repeats every year',
      },
    };
  }

  return {
    match: '',
    recurrence: buildOneOffRecurrence(),
  };
}

function applyPreset(preset: ReminderPreset, now: Date) {
  if (preset === 'later_today') {
    const later = setTime(now, Math.max(now.getHours() + 3, 18), 0);
    return { date: toIsoDate(later), time: toTimeValue(later.getHours(), later.getMinutes()) };
  }

  if (preset === 'tomorrow_morning') {
    const tomorrow = setTime(addDays(now, 1), 9, 0);
    return { date: toIsoDate(tomorrow), time: '09:00' };
  }

  if (preset === 'next_week') {
    const nextWeek = setTime(addDays(now, 7), 9, 0);
    return { date: toIsoDate(nextWeek), time: '09:00' };
  }

  if (preset === 'next_month') {
    const nextMonth = setTime(addMonths(now, 1), 9, 0);
    return { date: toIsoDate(nextMonth), time: '09:00' };
  }

  return null;
}

function parseExplicitDate(normalized: string, now: Date) {
  const isoMatch = normalized.match(/\b(\d{4}-\d{2}-\d{2})\b/);
  if (isoMatch?.[1]) {
    return { match: isoMatch[0], date: isoMatch[1] };
  }

  const localMatch = normalized.match(/\b(\d{1,2})[\/-](\d{1,2})(?:[\/-](\d{2,4}))?\b/);
  if (localMatch) {
    const year = localMatch[3]
      ? localMatch[3].length === 2 ? `20${localMatch[3]}` : localMatch[3]
      : String(now.getFullYear());
    return {
      match: localMatch[0],
      date: `${year}-${pad(Number(localMatch[2]))}-${pad(Number(localMatch[1]))}`,
    };
  }

  return null;
}

function parseRelativeDate(normalized: string, now: Date) {
  const explicit = parseExplicitDate(normalized, now);
  if (explicit) return explicit;

  if (/\blater today\b/i.test(normalized)) {
    return { match: normalized.match(/\blater today\b/i)?.[0] || '', date: toIsoDate(now) };
  }

  if (/\btoday\b/i.test(normalized)) {
    return { match: normalized.match(/\btoday\b/i)?.[0] || '', date: toIsoDate(now) };
  }

  if (/\btomorrow\b/i.test(normalized)) {
    return { match: normalized.match(/\btomorrow\b/i)?.[0] || '', date: toIsoDate(addDays(now, 1)) };
  }

  if (/\bnext week\b/i.test(normalized)) {
    return { match: normalized.match(/\bnext week\b/i)?.[0] || '', date: toIsoDate(addDays(now, 7)) };
  }

  if (/\bnext month\b/i.test(normalized)) {
    return { match: normalized.match(/\bnext month\b/i)?.[0] || '', date: toIsoDate(addMonths(now, 1)) };
  }

  const weekdayMatch = normalized.match(/\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/i);
  if (weekdayMatch?.[1]) {
    const weekdayIndex = WEEKDAY_LABELS.findIndex((label) => label.toLowerCase() === weekdayMatch[1].toLowerCase());
    if (weekdayIndex >= 0) {
      const base = startOfLocalDay(now);
      const current = weekdayIndexFromDate(base);
      const offset = weekdayIndex > current ? weekdayIndex - current : 7 - (current - weekdayIndex);
      const candidate = addDays(base, offset === 0 ? 7 : offset);
      return { match: weekdayMatch[0], date: toIsoDate(candidate) };
    }
  }

  return null;
}

function parseTime(normalized: string) {
  const noonMatch = normalized.match(/\b(noon|midday)\b/i);
  if (noonMatch) {
    return { match: noonMatch[0], time: '12:00' };
  }

  const keywordMatch = normalized.match(/\b(morning|afternoon|evening|tonight)\b/i);
  if (keywordMatch) {
    const time = keywordMatch[1].toLowerCase() === 'morning'
      ? '09:00'
      : keywordMatch[1].toLowerCase() === 'afternoon'
        ? '14:00'
        : '18:00';
    return { match: keywordMatch[0], time };
  }

  const timeMatch = normalized.match(/\bat\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\b/i)
    || normalized.match(/\b(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/i);
  if (!timeMatch) return null;

  const hourRaw = Number(timeMatch[1]);
  const minute = Number(timeMatch[2] || 0);
  const meridiem = (timeMatch[3] || '').toLowerCase();
  let hour = hourRaw;

  if (meridiem === 'pm' && hour < 12) hour += 12;
  if (meridiem === 'am' && hour === 12) hour = 0;

  return {
    match: timeMatch[0],
    time: toTimeValue(Math.min(hour, 23), Math.min(minute, 59)),
  };
}

function tidyReminderTitle(value: string) {
  return normaliseInput(
    value
      .replace(/\b(remind me to|remind me|remember to)\b/gi, ' ')
      .replace(/\brepeat\b/gi, ' ')
      .replace(/[.,]+$/g, ' ')
      .replace(/\s+/g, ' '),
  );
}

function fallbackDateForRecurrence(recurrence: ReminderRecurrence, now: Date) {
  if (recurrence.recurrenceType !== 'custom') {
    const tomorrow = setTime(addDays(now, 1), 9, 0);
    return { date: toIsoDate(tomorrow), time: '09:00' };
  }

  if (recurrence.recurrenceUnit === 'days') {
    const next = setTime(addDays(now, recurrence.recurrenceInterval || 1), 9, 0);
    return { date: toIsoDate(next), time: '09:00' };
  }

  if (recurrence.recurrenceUnit === 'weeks') {
    const next = setTime(addDays(now, 7 * (recurrence.recurrenceInterval || 1)), 9, 0);
    return { date: toIsoDate(next), time: '09:00' };
  }

  const next = setTime(addMonths(now, recurrence.recurrenceUnit === 'years' ? 12 : 1), 9, 0);
  return { date: toIsoDate(next), time: '09:00' };
}

export function parseQuickReminderInput(
  rawInput: string,
  options?: {
    now?: Date;
    preset?: ReminderPreset | null;
    reminderStrength?: ReminderStrength;
  },
): ParsedQuickReminder {
  const now = options?.now ? cloneDate(options.now) : new Date();
  const normalized = normaliseInput(rawInput);
  const preset = options?.preset || null;
  const reminderStrength = options?.reminderStrength || 'standard';
  const presetValue = preset && preset !== 'custom' ? applyPreset(preset, now) : null;
  const recurrenceSeed = presetValue ? new Date(`${presetValue.date}T${presetValue.time || '09:00'}:00`) : now;
  const detectedRecurrence = detectRecurrence(normalized, recurrenceSeed);
  const detectedDate = presetValue ? null : parseRelativeDate(normalized, now);
  const detectedTime = presetValue ? null : parseTime(normalized);

  const fallback = fallbackDateForRecurrence(detectedRecurrence.recurrence, now);
  const date = presetValue?.date || detectedDate?.date || fallback.date;
  const time = presetValue?.time || detectedTime?.time || (detectedDate?.match && /morning/i.test(detectedDate.match) ? '09:00' : fallback.time);

  let titleSource = normalized;
  titleSource = removePhrase(titleSource, detectedRecurrence.match);
  titleSource = removePhrase(titleSource, detectedDate?.match || '');
  titleSource = removePhrase(titleSource, detectedTime?.match || '');
  const title = tidyReminderTitle(titleSource) || 'Reminder';

  return {
    rawInput: normalized,
    title,
    date,
    time,
    type: detectReminderType(title),
    captureKind: 'quick_reminder',
    reminderStrength,
    recurrence: detectedRecurrence.recurrence,
    preset,
    dateLabel: formatDisplayDate(date, time),
    repeatLabel: detectedRecurrence.recurrence.summary,
  };
}

export function getReminderStrengthLabel(value: ReminderStrength) {
  return REMINDER_STRENGTH_OPTIONS.find((option) => option.value === value)?.label || 'Standard';
}

export function getReminderStrengthDescription(value: ReminderStrength) {
  return REMINDER_STRENGTH_OPTIONS.find((option) => option.value === value)?.description || '';
}

export function isQuickReminderEvent(event: QuickReminderEventLike) {
  return (event.captureKind || event.capture_kind || 'event') === 'quick_reminder';
}

export function getReminderStrength(event: QuickReminderEventLike): ReminderStrength {
  return (event.reminderStrength || event.reminder_strength || 'once') as ReminderStrength;
}

export function buildQuickReminderSubtitle(event: QuickReminderEventLike) {
  const parts = [`${getReminderStrengthLabel(getReminderStrength(event))} reminder`];
  if (event.time) {
    parts.push((event.time || '').slice(0, 5));
  }
  return parts.join(' · ');
}

export function getQuickReminderAttention(event: QuickReminderEventLike, now = new Date()) {
  if (!isQuickReminderEvent(event)) return null;

  const dueDate = new Date(`${event.date}T${event.time || '09:00'}:00`);
  const daysUntil = differenceInDays(dueDate, now);
  const strength = getReminderStrength(event);
  const strengthOffset = strength === 'pester' ? 12 : strength === 'standard' ? 6 : 0;
  const score = daysUntil < 0
    ? 88 + strengthOffset
    : daysUntil === 0
      ? 74 + strengthOffset
      : daysUntil <= 2
        ? 60 + strengthOffset
        : daysUntil <= 7
          ? 45 + strengthOffset
          : 0;

  if (score === 0) return null;

  const timeLabel = event.time ? ` at ${(event.time || '').slice(0, 5)}` : '';
  const text = daysUntil < 0
    ? `${event.title} is overdue`
    : daysUntil === 0
      ? `${event.title} is due today${timeLabel}`
      : daysUntil === 1
        ? `${event.title} is due tomorrow${timeLabel}`
        : `${event.title} is due in ${daysUntil} days${timeLabel}`;

  return {
    daysUntil,
    score,
    text,
  };
}

export function buildQuickReminderSectionNudge(events: QuickReminderEventLike[], now = new Date()) {
  const candidate = [...events]
    .filter((event) => isQuickReminderEvent(event))
    .map((event) => ({
      event,
      attention: getQuickReminderAttention(event, now),
    }))
    .filter((entry): entry is { event: QuickReminderEventLike; attention: NonNullable<ReturnType<typeof getQuickReminderAttention>> } => Boolean(entry.attention))
    .sort((left, right) => right.attention.score - left.attention.score)[0];

  if (!candidate) return null;

  return candidate.attention.daysUntil < 0
    ? `${candidate.event.title} is overdue — clear it from Calendar before it slips further.`
    : `${candidate.event.title} is sitting in Calendar for ${candidate.attention.daysUntil === 0 ? 'today' : 'the next few days'} — clear it before it drifts.`;
}
